import * as React from 'react';
import { useSelector } from 'react-redux';
import { lookupsLoadingFinishedSelector } from 'store/lookups/selector';
import useDebouncedState from 'hooks/useDebouncedState';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { PrimaryButton } from '@fluentui/react';
import moment from 'moment-timezone';
import { SelectOption } from 'formComponents/Select/types';
import { Select } from 'formComponents/Select/Select';
import { Label } from '@fluentui/react/lib/Label';
import range from 'lodash/range';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { formStyles } from 'styles/formStyles';
import { generateReportPrintUrl } from '../utils';
import { ReclayZahlung } from './ReclayZahlung';

const styles = mergeStyleSets({
    container: {
        margin: 0,
        marginTop: 5,
        padding: 0,
        display: 'grid',
        gridTemplateRows: 'max-content 1fr',
        gridRowGap: 10,
        height: 'calc(100vh - 75px)',
    },
    searchLine: {
        display: 'grid',
        gridTemplateColumns: '160px 160px max-content 1fr  max-content',
        gridColumnGap: 10,
        marginTop: 10,
    },
    actionButtons: {
        marginTop: 29,
        display: 'grid',
        gridTemplateColumns: 'repeat(2, max-content)',
        gridColumnGap: 10,
    },
});

interface FilterValues {
    abrJahr: number;
    abrMo: number;
    showDetails: boolean;
}

const defaultFilterValues: FilterValues = {
    abrJahr: moment().year(),
    abrMo: moment().month() + 1,
    showDetails: false,
};

const yearOptions = range(moment().year(), moment().year() - 25).map((year) => ({
    value: year.toString(),
    label: year.toString(),
}));

const monthOptions = range(0, 12).map((month) => ({
    value: (month + 1).toString(),
    label: (month + 1).toString(),
}));

export const ReclayZahlungView: React.FC = () => {
    const lookupsLoadingFinished = useSelector(lookupsLoadingFinishedSelector);
    const [filterValues, debouncedFilterValues, setFilterValues] = useDebouncedState<FilterValues>({
        ...defaultFilterValues,
    });

    const selectedYearOption = React.useMemo<SelectOption>(
        () => ({ value: filterValues.abrJahr.toString(), label: filterValues.abrJahr.toString() }),
        [filterValues.abrJahr],
    );

    const selectedMonthOption = React.useMemo<SelectOption>(
        () => ({ value: filterValues.abrMo.toString(), label: filterValues.abrMo.toString() }),
        [filterValues.abrMo],
    );

    if (!lookupsLoadingFinished) {
        return <div />;
    }

    return (
        <div className={styles.container}>
            <div className={styles.searchLine}>
                <div>
                    <Label>Jahr</Label>
                    <Select
                        name="Jahr"
                        options={yearOptions}
                        value={selectedYearOption}
                        onChange={(newOption) => {
                            setFilterValues({
                                ...filterValues,
                                abrJahr: parseInt(newOption ? newOption.value : moment().year().toString(), 10),
                            });
                        }}
                    />
                </div>
                <div>
                    <Label>Monat</Label>
                    <Select
                        name="Monat"
                        options={monthOptions}
                        value={selectedMonthOption}
                        onChange={(newOption) => {
                            setFilterValues({
                                ...filterValues,
                                abrMo: parseInt(newOption ? newOption.value : (moment().month() + 1).toString(), 10),
                            });
                        }}
                    />
                </div>

                <div>
                    <Checkbox
                        className={formStyles.checkboxInsideFormAligned}
                        label="Details anzeigen"
                        checked={filterValues.showDetails}
                        onChange={(event, checked) => {
                            setFilterValues({
                                ...filterValues,
                                showDetails: !!checked,
                            });
                        }}
                    />
                </div>

                <div />

                <PrimaryButton
                    onClick={() => {
                        window.open(
                            generateReportPrintUrl('reclayzahlung', {
                                abrJahr: filterValues.abrJahr || 2021,
                                abrMo: filterValues.abrMo || 12,
                                showDetails: filterValues.showDetails,
                            }),
                        );
                    }}
                    text="Drucken"
                />
            </div>

            <ReclayZahlung
                abrJahr={debouncedFilterValues.abrJahr}
                abrMo={debouncedFilterValues.abrMo}
                showDetails={debouncedFilterValues.showDetails}
            />
        </div>
    );
};
